import { getCookie } from '@avalara/bat-apps-shared/helpers/cookies';
import { CYBERSOURCE_CANCEL_MESSAGE_TYPE, CYBERSOURCE_SUBMIT_MESSAGE_TYPE } from '@avalara/types/cybersource';

import { clearSystemError, setNeedToFetchCybersourceData } from '../store/system';
import analytics from './analytics';
import { getSystemErrorMessaging } from './side-effects/error';
import { goToNextOfferStep } from './side-effects/offer-flow';
import { purchaseOffer } from './side-effects/offers';

import type { Dispatch } from '@avalara/types/actions';
import type { InitialAddObligationsOptions } from '@avalara/types/index';
import type { RouteComponentProps } from 'react-router-dom';

export type CybersourceCallback = {
  message?: string;
  paymentMethodId?: string;
  success?: boolean;
  type: string;
};

export type CybersourceMessageHandlerContext = {
  dispatch: Dispatch;
  history: RouteComponentProps['history'];
  isLoggedIn: boolean;
  isHighRadiusEnabled: boolean;
  offerFlow: unknown;
  paymentView?: string;
  isEmbedded?: boolean;
  onSuccess?: () => void;
  onAddObligation: (options: InitialAddObligationsOptions) => void;
  onAddCreditCard: (paymentMethodToken: string) => void;
  onHandlePaymentError: (message?: string) => void;
};

const shouldIgnoreCybersourceCallback = (isLoggedIn: boolean, isHighRadiusEnabled: boolean): boolean =>
  isLoggedIn && isHighRadiusEnabled;

const handleE2eBypass = (ctx: CybersourceMessageHandlerContext): boolean => {
  if (!getCookie('TESTCAFE_E2E')) return false;

  if (ctx.offerFlow) {
    ctx.dispatch(goToNextOfferStep({ history: ctx.history }));
  } else {
    ctx.onAddObligation({ paymentToken: 'test', history: ctx.history });
  }
  return true;
};

const handleCybersourceCancel = (ctx: CybersourceMessageHandlerContext): void => {
  ctx.dispatch(setNeedToFetchCybersourceData(true));
  // NOTE: This takes you back to the previous page in the flow.
  // Have to use .go(-3) (.go(-1), .go(-2), and .goBack() shows session timeout in the iframe)
  ctx.history.go(-3);
};

const handleOfferFlowPayment = (ctx: CybersourceMessageHandlerContext, paymentToken: string): void => {
  ctx.onAddCreditCard(paymentToken);
  if (ctx.isEmbedded) {
    ctx.dispatch(goToNextOfferStep({ history: ctx.history }));
    return;
  }
  ctx.dispatch(purchaseOffer(ctx.history, paymentToken));
};

const handleSuccessfulPayment = (ctx: CybersourceMessageHandlerContext, paymentToken: string): void => {
  const options: InitialAddObligationsOptions = {
    history: ctx.history,
    paymentToken,
    handleError: ctx.onHandlePaymentError,
  };

  ctx.dispatch(clearSystemError());

  if (ctx.paymentView === 'add-credit-card') {
    ctx.onAddCreditCard(paymentToken);
    ctx.onSuccess?.();
    return;
  }

  if (ctx.paymentView === 'offer' || ctx.offerFlow) {
    // Returns flows use offer paymentView, so we also need to check if offerFlow is set
    handleOfferFlowPayment(ctx, paymentToken);
    return;
  }

  ctx.onAddObligation(options);
};

const handleFailedPayment = (ctx: CybersourceMessageHandlerContext, message?: string): void => {
  ctx.dispatch(setNeedToFetchCybersourceData(true));
  ctx.dispatch(getSystemErrorMessaging({ errorCode: 'failedCybersourcePayment', msg: message || '' }));

  analytics.track('CYBERSOURCE_PAYMENT_SUBMIT_ERROR', {
    error: true,
    message,
    // Note: These are resources for troubleshooting the errors.
    errorResources: {
      cybersource:
        'https://developer.cybersource.com/library/documentation/dev_guides/Secure_Acceptance_Hosted_Checkout/html/Topics/Reason_Codes.htm',
    },
  });
};

const handleCybersourceSubmit = (ctx: CybersourceMessageHandlerContext, data: CybersourceCallback): void => {
  const { message, paymentMethodId, success } = data;
  if (success) {
    handleSuccessfulPayment(ctx, paymentMethodId || '');
    return;
  }
  handleFailedPayment(ctx, message);
};

export const handleCybersourcePostMessage = (
  msg: MessageEvent<CybersourceCallback>,
  ctx: CybersourceMessageHandlerContext,
): void => {
  if (shouldIgnoreCybersourceCallback(ctx.isLoggedIn, ctx.isHighRadiusEnabled)) return;
  if (handleE2eBypass(ctx)) return;

  const { type } = msg.data || {};
  if (type === CYBERSOURCE_CANCEL_MESSAGE_TYPE) {
    handleCybersourceCancel(ctx);
    return;
  }
  if (type === CYBERSOURCE_SUBMIT_MESSAGE_TYPE) {
    handleCybersourceSubmit(ctx, msg.data || { type });
  }
};
